import { jsx, jsxs } from 'react/jsx-runtime';
import { Breadcrumbs } from '../page-layout/Breadcrumbs.js';
import { Button } from '../page-layout/Button.js';
import { Header, HeaderTitle, HeaderDescription } from '../page-layout/Header.js';
import { PageActions } from '../page-layout/PageActions.js';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import 'clsx';
import 'tailwind-merge';
import '@radix-ui/react-label';
import '../ui/button.js';
import '@radix-ui/react-progress';
import '../../contexts/AppContext.js';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { forwardRef, useState, useImperativeHandle, useEffect } from 'react';
import '../../lib/toast.js';
import '../page-layout/FormPageLayout.js';
import { InlineContainer } from '../page-layout/InlineContainer.js';
import { Stack } from '../page-layout/Stack.js';
import 'cmdk';
import '@radix-ui/react-dialog';
import '@radix-ui/react-popover';
import '../form/RichTextEditor.js';
import { useApiClient } from '../../lib/api-client.js';
import { useQuery } from '@tanstack/react-query';
import '@radix-ui/react-switch';
import './CrudFormView.js';
import '../../contexts/OrganizationContext.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import 'react-router-dom';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';
import { DeleteRecordDialog } from './DeleteRecordDialog.js';
import { Badge } from '../ui/badge.js';
import 'react-day-picker';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card.js';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible.js';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import { ErrorState } from '../ui/error-state.js';
import '../ui/radio-group.js';
import { RichTextContent } from '../ui/RichTextContent.js';
import '@radix-ui/react-select';
import { Separator } from '../ui/separator.js';
import '../ui/sidebar.js';
import { Skeleton } from '../ui/skeleton.js';
import 'react-toastify';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';

/**
 * Generic CRUD detail view component that displays a single item with all its details
 * @param config - CRUD configuration object defining fields and sections
 * @param itemId - ID of the item to display
 * @param onBack - Optional callback when back button is clicked
 * @param onEdit - Optional callback when edit button is clicked
 * @param onDelete - Optional callback when delete button is clicked
 * @param breadcrumbs - Optional breadcrumb items for navigation context
 * @param className - Additional CSS classes
 * @param ref - Optional ref to expose refetch function
 */
const CrudDetailView = forwardRef(function CrudDetailView({ config, itemId, onBack, onEdit, onDelete, onData, breadcrumbs, className = '', customActions = [], }, ref) {
    const apiClient = useApiClient();
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        loading: false,
    });
    // Use sections from config (required)
    const sections = config.detailView.sections;
    // Fetch item data
    const { data: response, isLoading, error, refetch, } = useQuery({
        queryKey: [config.entityName, 'detail', itemId],
        queryFn: async () => {
            const result = await apiClient.get(config.endpoints.single(itemId));
            // Validate response format
            if (typeof result !== 'object' || result === null) {
                throw new Error('Invalid response format: expected JSON object');
            }
            if (!('success' in result) || !('data' in result)) {
                throw new Error('Invalid response structure: missing required fields (success, data)');
            }
            return result;
        },
    });
    const item = response?.success ? response.data : null;
    // Expose refetch function via ref
    useImperativeHandle(ref, () => ({
        refetch: async () => {
            await refetch();
        },
    }));
    // Call onData callback when item is loaded
    useEffect(() => {
        if (item && onData) {
            onData(item);
        }
    }, [item, onData]);
    /**
     * Toggle section expansion
     */
    const toggleSection = (sectionId) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
        }
        else {
            newExpanded.add(sectionId);
        }
        setExpandedSections(newExpanded);
    };
    /**
     * Handle delete confirmation
     */
    const handleConfirmDelete = async () => {
        if (!item || !onDelete)
            return;
        setConfirmDialog(prev => ({ ...prev, loading: true }));
        try {
            await onDelete(item);
        }
        catch (error) {
            console.error('Error deleting item:', error);
        }
        finally {
            setConfirmDialog({
                open: false,
                loading: false,
            });
        }
    };
    /**
     * Format field value based on field configuration
     */
    const formatFieldValue = (field, value, fullItem) => {
        // Use custom formatter if provided
        if (field.format) {
            return field.format(value, fullItem);
        }
        // Default formatting based on field type
        switch (field.type) {
            case 'boolean':
                return value ? (jsx(Badge, { variant: "secondary", children: "Yes" })) : (jsx(Badge, { variant: "outline", children: "No" }));
            case 'date':
                return value ? new Date(value).toLocaleDateString() : '-';
            case 'datetime':
                return value ? new Date(value).toLocaleString() : '-';
            case 'decimal':
                return typeof value === 'number' ? `$${value.toFixed(2)}` : value;
            case 'number':
                return typeof value === 'number' ? value.toLocaleString() : value;
            case 'tags':
                if (Array.isArray(value)) {
                    return (jsx("div", { className: "flex flex-wrap gap-2", children: value.map((tag, idx) => (jsx(Badge, { variant: "outline", children: tag }, idx))) }));
                }
                return value;
            case 'json':
                return (jsx("pre", { className: "bg-muted p-3 rounded-md text-sm overflow-auto max-h-40", children: JSON.stringify(value, null, 2) }));
            case 'textarea':
                return jsx("div", { className: "whitespace-pre-wrap bg-muted p-3 rounded-md", children: value || '-' });
            case 'richtext':
                return jsx(RichTextContent, { content: value });
            case 'image':
                return value ? (jsx("img", { src: `/api/v1/media/${value}`, alt: field.label, className: "max-w-md rounded-xl border" })) : ('-');
            case 'audio':
                return value ? (jsxs("audio", { controls: true, className: "w-full max-w-md", children: [jsx("source", { src: `/api/v1/media/${value}` }), "Your browser does not support the audio element."] })) : ('-');
            case 'status':
                if (value === 'ACTIVE') {
                    return jsx(Badge, { variant: "default", children: "Active" });
                }
                else if (value === 'INACTIVE') {
                    return jsx(Badge, { variant: "secondary", children: "Inactive" });
                }
                else if (value === 'DISCONTINUED') {
                    return jsx(Badge, { variant: "destructive", children: "Discontinued" });
                }
                return jsx(Badge, { variant: "outline", children: value });
            default:
                return value?.toString() || '-';
        }
    };
    if (error) {
        return (jsx("div", { className: className, children: jsxs(Stack, { direction: "vertical", gap: "md", children: [breadcrumbs && jsx(Breadcrumbs, { items: breadcrumbs }), jsx(ErrorState, { title: `Failed to load ${config.entityName.toLowerCase()}`, error: error, action: {
                            label: 'Retry',
                            onClick: () => refetch(),
                        } })] }) }));
    }
    return (jsxs("div", { className: className, children: [jsxs(Stack, { direction: "vertical", gap: "md", children: [breadcrumbs && jsx(Breadcrumbs, { items: breadcrumbs }), onBack && !breadcrumbs && (jsx("div", { className: "flex justify-start", children: jsx(Button, { variant: "link", onClick: onBack, icon: ArrowLeft, className: "p-0 h-auto", children: "Back" }) })), isLoading ? (jsxs("div", { className: "space-y-2", children: [jsx(Skeleton, { className: "h-8 w-64" }), jsx(Skeleton, { className: "h-4 w-96" })] })) : item ? (jsxs(InlineContainer, { children: [jsxs(Header, { children: [jsx(HeaderTitle, { children: item ? item.name || `${config.entityName} Details` : config.entityName }), jsxs(HeaderDescription, { children: ["View ", config.entityName.toLowerCase(), " details and information"] })] }), jsxs(PageActions, { align: "right", children: [customActions.map(action => (jsx(Button, { variant: action.variant || 'outline', size: "sm", onClick: action.onClick, icon: action.icon, loading: action.loading, children: action.label }, action.key))), onEdit && (jsx(Button, { variant: "outline", size: "sm", onClick: () => onEdit(item), icon: Edit, children: "Edit" })), onDelete && (jsx(Button, { variant: "destructive", size: "sm", onClick: () => setConfirmDialog({ open: true, loading: false }), icon: Trash2, children: "Delete" }))] })] })) : null, jsx("div", { className: "grid gap-6", children: sections.map(section => {
                            const sectionFields = section.fields
                                .map(fieldKey => config.fields.find(field => field.key === fieldKey))
                                .filter(field => field !== undefined);
                            if (sectionFields.length === 0)
                                return null;
                            const sectionId = section.title.toLowerCase().replace(/\s+/g, '-');
                            const isExpanded = expandedSections.has(sectionId) ||
                                (!section.collapsible && section.defaultExpanded !== false);
                            const SectionWrapper = section.collapsible ? Collapsible : 'div';
                            const sectionProps = section.collapsible
                                ? {
                                    open: isExpanded,
                                    onOpenChange: () => toggleSection(sectionId),
                                }
                                : {};
                            return (jsx(Card, { children: jsxs(SectionWrapper, { ...sectionProps, children: [jsx(CardHeader, { className: section.collapsible ? 'cursor-pointer' : '', children: section.collapsible ? (jsx(CollapsibleTrigger, { asChild: true, children: jsx("div", { className: "flex items-center justify-between", children: jsxs("div", { children: [jsx(CardTitle, { children: section.title }), section.description && (jsx(CardDescription, { children: section.description }))] }) }) })) : (jsxs("div", { children: [jsx(CardTitle, { children: section.title }), section.description && (jsx(CardDescription, { children: section.description }))] })) }), section.collapsible ? (jsx(CollapsibleContent, { children: jsx(CardContent, { className: "space-y-4", children: isLoading
                                                    ? // Loading skeleton
                                                        sectionFields.map(field => (jsxs("div", { className: "space-y-2", children: [jsx(Skeleton, { className: "h-4 w-24" }), jsx(Skeleton, { className: "h-6 w-full" })] }, field.key)))
                                                    : // Field data
                                                        sectionFields.map((field, index) => (jsxs("div", { children: [jsxs("div", { className: "grid grid-cols-3 gap-4", children: [jsx("div", { className: "font-medium text-muted-foreground", children: field.label }), jsx("div", { className: "col-span-2", children: formatFieldValue(field, item?.[field.key], item ?? undefined) })] }), index < sectionFields.length - 1 && (jsx(Separator, { className: "mt-4" }))] }, field.key))) }) })) : (jsx(CardContent, { className: "space-y-4", children: isLoading
                                                ? // Loading skeleton
                                                    sectionFields.map(field => (jsxs("div", { className: "space-y-2", children: [jsx(Skeleton, { className: "h-4 w-24" }), jsx(Skeleton, { className: "h-6 w-full" })] }, field.key)))
                                                : // Field data
                                                    sectionFields.map((field, index) => (jsxs("div", { children: [jsxs("div", { className: "grid grid-cols-3 gap-4", children: [jsx("div", { className: "font-medium text-muted-foreground", children: field.label }), jsx("div", { className: "col-span-2", children: formatFieldValue(field, item?.[field.key], item ?? undefined) })] }), index < sectionFields.length - 1 && jsx(Separator, { className: "mt-4" })] }, field.key))) }))] }) }, sectionId));
                        }) })] }), jsx(DeleteRecordDialog, { open: confirmDialog.open, onOpenChange: open => setConfirmDialog(prev => ({ ...prev, open })), entityName: config.entityName, loading: confirmDialog.loading, onConfirm: handleConfirmDelete })] }));
});

export { CrudDetailView };
//# sourceMappingURL=CrudDetailView.js.map
